import { templateQueryOptions } from "@/hooks/useTemplates"
import { queryClient } from "@/lib/queryClient"
import { localizeTextPrompt } from "@/lib/i18n-content"
import { matchTemplateParameters } from "@/lib/template-parameters"
import type { ApplyableSettings } from "@/types/generation"

/**
 * 템플릿으로 만든 생성을 재사용(내 이미지 / 공유 / 커뮤니티)할 때 파라미터 칩 모드를 복원한다.
 *
 * DB 에는 파라미터를 채운 프롬프트만 남으므로, 템플릿 원문(현재 UI 언어 → ko → 나머지 번역
 * 순)과 맞춰 보고 맞는 원문과 값으로 설정을 바꾼다. 맞는 원문이 없거나(직접 수정, 템플릿
 * 변경·비활성화) 조회에 실패하면 설정을 그대로 돌려준다 (일반 텍스트로 적용).
 */
export async function restoreTemplateParameters(
  settings: ApplyableSettings
): Promise<ApplyableSettings> {
  if (!settings.template) return settings

  let template
  try {
    // 비활성 템플릿은 404 — 재시도로 적용을 늦추지 않는다
    template = await queryClient.fetchQuery({
      ...templateQueryOptions(settings.template.id),
      retry: false,
    })
  } catch {
    return settings
  }

  const candidates = new Set([
    localizeTextPrompt(template),
    template.textPrompt,
    ...Object.values(template.textPromptI18n ?? {}),
  ])
  for (const rawPrompt of candidates) {
    const values = matchTemplateParameters(rawPrompt, settings.prompt)
    if (values) {
      return { ...settings, prompt: rawPrompt, templateParameters: values }
    }
  }
  return settings
}
